/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormInput, CFormLabel, CFormTextarea,
  CButton, CSpinner, CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from '../../hooks/useTranslation';

const CompositionInputs = ({
  onSubmit, onCancel, isLoading = false,
  submitResponse = null, initialData = null, isEdit = false
}) => {
  const [formData, setFormData] = useState({
    threadName: '',
    threadDescription: ''
  });
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialData) {
      setFormData({
        threadName: initialData.ThreadName || '',
        threadDescription: initialData.ThreadDescription || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => {
        if (!isEdit) {
          setFormData({
            threadName: '',
            threadDescription: ''
          });
        }
        setShowSuccess(false);
      }, 2000);
    }
  }, [submitResponse, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.threadName.trim()) {
      newErrors.threadName = t('threadNameRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const getSuccessMessage = () => {
    return isEdit ? t('compositionUpdated') : t('compositionCreated');
  };

  const getButtonText = () => {
    if (isLoading) {
      return isEdit ? t('updating') : t('creating');
    }
    return isEdit ? t('updateComposition') : t('createComposition');
  };

  const handleCancel = () => {
    if (!isEdit) setFormData({ threadName: '', threadDescription: '' });
    setErrors({});
    onCancel();
  };

  return (
    <div>
      {showSuccess && (
        <CAlert color="success" dismissible onClose={() => setShowSuccess(false)}>
          {getSuccessMessage()}
        </CAlert>
      )}

      {errors.general && (
        <CAlert color="danger" dismissible onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
          {errors.general}
        </CAlert>
      )}

      {submitResponse && submitResponse.Result && submitResponse.Result.ErrorCode !== 0 && submitResponse.Result.ErrorDescription && (
        <CAlert color="danger">
          {submitResponse.Result.ErrorDescription}
        </CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="threadName">
              {t('threadCompositionName')}
            </CFormLabel>
            <CFormInput
              type="text"
              id="threadName"
              name="threadName"
              value={formData.threadName}
              onChange={handleInputChange}
              invalid={!!errors.threadName}
              disabled={isLoading}
              placeholder={t('threadCompositionDescription')}
            />
            {errors.threadName && (
              <div className="invalid-feedback d-block">
                {errors.threadName}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={12}>
            <CFormLabel htmlFor="threadDescription">
              {t('threadCompositionDescription')}
            </CFormLabel>
            <CFormTextarea
              id="threadDescription"
              name="threadDescription"
              rows={4}
              value={formData.threadDescription}
              onChange={handleInputChange}
              invalid={!!errors.threadDescription}
              disabled={isLoading}
              placeholder={t('enterThreadCompositionDescription')}
            />
            {errors.threadDescription && (
              <div className="invalid-feedback d-block">
                {errors.threadDescription}
              </div>
            )}
          </CCol>
        </CRow>

        <CRow>
          <CCol className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  {getButtonText()}
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  {getButtonText()}
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={handleCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              {t('cancel')}
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

CompositionInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default CompositionInputs;
